import React, { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { PickedMeetingLocation } from '@chilipiper/api-client/src/chat-platform-chat'
import {
  FlexibleRoundRobinMeetingStartTimes,
  UsersCombinedMeetingStartTimes,
} from '@chilipiper/api-client/src/user-availability'
import { buildReserveTimeSlotPayloadDeprecated } from '@chilipiper/booking-flow'
import { useMeeting, useReserveTimeSlot } from '../../../api-queries/chat-platform-chat'
import { BookMeetingBlock as BookMeetingBlockType } from '../../../chat-data/types'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { useMeetingTypeResolution } from '../../../api-queries/useMeetingTypeResolution'
import { useLoadMeetingRelatedData } from './hooks'
import { BookingFlowBlock } from './BookingFlowBlock'
import { ExpiredBooking } from './ExpiredBooking'
import { BookingFlowBlockLoading } from './BookingFlowBlockLoading'

interface Props {
  block: BookMeetingBlockType
}

export const BookMeetingBlock = ({ block }: Props) => {
  if (block.cancelled) {
    return <ExpiredBooking />
  }
  return <BookMeetingBlockNotCancelled block={block} />
}

const getAssigneesBySelectedTimeSlot = (
  selectedTime: string,
  timeSlots?: FlexibleRoundRobinMeetingStartTimes | UsersCombinedMeetingStartTimes
) => {
  if (!timeSlots) {
    return []
  }
  if ('userIds' in timeSlots) {
    return timeSlots.userIds
  }
  const assignees = timeSlots?.startTimes?.find(
    timeSlot => timeSlot.startTime === selectedTime
  )?.availableAssignees
  return assignees ?? []
}

const BookMeetingBlockNotCancelled = ({ block }: Props) => {
  const { selectMeetingSlot, tenantId = '' } = useMessageFeedContext()
  const {
    meetingType,
    meetingTypeId,
    isLoading: isLoadingMeetingType,
  } = useMeetingTypeResolution(tenantId, block.meetingTypeId)
  const now = useRef(new Date())
  const [isRescheduling, setIsRescheduling] = useState(false)

  const { timeSlots, refetchAvailability, isLoading, distribution } = useLoadMeetingRelatedData({
    meetingTypeId,
    assignees: block.assignees,
    now: now.current,
    distributionId: block.distributionId,
    isRescheduling,
  })
  const { data: meeting } = useMeeting(tenantId, block.meetingId)

  const {
    mutate: selectSlot,
    isPending: isSelectingSlot,
    isSuccess: isSlotSelected,
  } = useMutation({
    mutationFn: async (data: Parameters<typeof selectMeetingSlot>[0]) =>
      selectMeetingSlot(data) ?? null,
  })

  const { mutateAsync: reserveTimeSlot, isPending: isReservingTimeSlot } =
    useReserveTimeSlot(tenantId)
  const bookMeetingThroughWidget = ({
    reservationId,
    duration,
    selectedTime,
    email,
    timezone,
  }: {
    duration: string
    email: string
    reservationId?: string
    selectedTime: string
    timezone: string
  }) => {
    const commonData = {
      email,
      timezone,
      interval: { duration, startsAt: selectedTime },
      pickedMeetingLocation: {
        type: meetingType?.meetingInvite.locations.default.type ?? 'DefinedInMeetingType',
      } as PickedMeetingLocation,
    } as const

    const assigneesBySelectedTimeSlot = getAssigneesBySelectedTimeSlot(selectedTime, timeSlots)
    selectSlot(
      reservationId && assigneesBySelectedTimeSlot[0]
        ? {
            ...commonData,
            type: 'MeetingSlotSelectedWithReservation',
            assigneeId: assigneesBySelectedTimeSlot[0],
            reservationId,
          }
        : {
            ...commonData,
            type: 'MeetingSlotSelectedWithoutReservation',
            assigneeIds: assigneesBySelectedTimeSlot,
          }
    )
  }

  if (isLoadingMeetingType) {
    return <BookingFlowBlockLoading state={block.meetingId ? 'scheduled' : 'scheduling'} />
  }

  return (
    <BookingFlowBlock
      isRescheduling={isRescheduling}
      isFlexibleReassignment={distribution?.reassignmentType === 'AnyTeamMember'}
      onReschedulingChange={setIsRescheduling}
      claimMeetingAttemptReservationError={{}}
      fields={[
        {
          dataType: {
            type: 'Email',
          },
          id: 'PersonEmail',
          isRequired: true,
          label: 'Email',
        },
      ]}
      meetingInvite={meetingType?.meetingInvite}
      block={block}
      onBookMeeting={({ selectedTime, onError, timezone, guestFormData }) => {
        const guestFormEmail = guestFormData['PersonEmail']
        const duration = meetingType?.meetingInvite.duration
        const email = Array.isArray(guestFormEmail) ? guestFormEmail[0] : guestFormEmail
        if (!email || !duration) {
          return
        }
        if (block.assignees.length !== 1) {
          return bookMeetingThroughWidget({ email, duration, selectedTime, timezone })
        }

        reserveTimeSlot(
          buildReserveTimeSlotPayloadDeprecated({
            meetingTypeRef: { id: meetingTypeId, timestamp: new Date().toISOString() },
            slotStartsAt: selectedTime,
            userIds: block.assignees,
          })
        )
          .then(({ id }) => {
            bookMeetingThroughWidget({
              reservationId: id,
              duration,
              selectedTime,
              email,
              timezone,
            })
          })
          .catch(() => {
            onError()
          })
      }}
      isMeetingReserved={isSlotSelected}
      isBookingMeeting={isReservingTimeSlot || isSelectingSlot}
      guestEmail={block.email ?? meeting?.primaryGuest.value}
      isLoading={isLoading}
      initialTimezone={
        meetingType?.forceGuestTimezone.type === 'Enabled'
          ? meetingType.forceGuestTimezone.zoneId
          : undefined
      }
      refetchAvailability={refetchAvailability}
      timeSlots={timeSlots}
    />
  )
}
